import { useEffect, useState, useCallback } from 'react';
import { getErrorMessage } from '@/lib/utils';
import type { ServerPaginationConfig } from '@/components/ui/DataTable';

interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; pageSize: number; total: number };
}

interface UseServerPaginatedQueryResult<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  filters: Record<string, string>;
  serverPagination: ServerPaginationConfig | undefined;
  handleFilterChange: (newFilters: Record<string, string>) => void;
  showInitialLoading: boolean;
}

export function useServerPaginatedQuery<T>(
  fetcher: (params: { page: number; pageSize: number; filters: Record<string, string> }) => Promise<PaginatedResponse<T>>,
  defaultPageSize: number,
  errorMessage: string,
): UseServerPaginatedQueryResult<T> {
  const [response, setResponse] = useState<PaginatedResponse<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher({ page, pageSize, filters });
      setResponse(result);
    } catch (err: unknown) {
      setError(getErrorMessage(err, errorMessage));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, fetcher, errorMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = (newFilters: Record<string, string>): void => {
    setFilters(newFilters);
    setPage(1);
  };

  const serverPagination: ServerPaginationConfig | undefined = response
    ? {
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        total: response.pagination.total,
        onPageChange: setPage,
        onPageSizeChange: (newSize: number) => { setPage(1); setPageSize(newSize); },
      }
    : undefined;

  return {
    data: response?.data ?? [],
    loading,
    error,
    filters,
    serverPagination,
    handleFilterChange,
    showInitialLoading: loading && !response,
  };
}
